/**
 * The household / individual switch, in the command bar.
 *
 * Deliberately prominent: every figure on screen changes meaning when this moves, and a
 * user who forgets they are scoped to one person will misread their own dashboard. It
 * shows the active scope as text rather than only an icon for that reason.
 */
import { CheckIcon, UsersIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useMembers } from '@/lib/queries'
import { cn } from '@/lib/utils'
import { useScope } from './scope'

export function ScopeSwitch() {
  const { memberId, setMemberId, isHousehold } = useScope()
  const members = useMembers()

  const rows = members.data ?? []
  const active = rows.find((member) => member.id === memberId)
  const label = isHousehold ? 'Household' : (active?.displayName ?? 'Household')

  // With nobody to attribute to, the switch would offer only its own default.
  if (rows.length === 0) return null

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('gap-1.5', !isHousehold && 'border-cyan/40 text-cyan')}
        >
          <UsersIcon aria-hidden className="size-3.5" />
          <span className="max-w-28 truncate">{label}</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="eyebrow">Show figures for</DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem onSelect={() => setMemberId(null)}>
          <CheckIcon aria-hidden className={cn('size-4', !isHousehold && 'opacity-0')} />
          <span className="flex-1">Whole household</span>
        </DropdownMenuItem>

        {rows.map((member) => (
          <DropdownMenuItem key={member.id} onSelect={() => setMemberId(member.id)}>
            <CheckIcon aria-hidden className={cn('size-4', memberId !== member.id && 'opacity-0')} />
            <span className="flex-1 truncate">{member.displayName}</span>
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />
        <p className="text-dim px-2 py-1.5 text-[0.6875rem] leading-snug">
          Narrows every screen to one person's transactions and accounts. It does not hide
          anything from anyone — it is a view, not a permission.
        </p>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
